import { Button, Grid, Box, Typography, Fab, useTheme, useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useMemo, useState, useCallback } from "react";

import { SnackbarMessenger } from "src/general_components/SnackbarMessenger";
import { GenericCard } from "src/general_components/GenericCard";
import { LoadingOverlay } from "src/general_components/LoadingOverlay";
import { ABMGenerico } from "src/general_components/ABMGenerico";

import Swal from "sweetalert2";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  deleteUser,
  getUsers2,
  getUsersPaginated,
} from "../general_helpers/UsersData";

import { ListTablePaginated } from "./ListTablePaginated";
import { SimpleDialogComponent } from "./SimpleDialogComponent";
import { UserForm } from "./UsersForm";
import { PageHeaderCard } from "src/general_components/PageHeaderCard";
import PeopleIcon from "@mui/icons-material/People";

import esArray, { obtenerProcesosPorPerfilYMenu } from "./functionEsArray";

export const UsersList = () => {
  const [userSelect, setUserSelect] = useState({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [sectorModalOpen, setSectorModalOpen] = useState(false);

  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [snackbarVisible, setSnackbarVisible] = useState(false);

  const [url_cognito, setUrlCognito] = useState("");
  const [owner_code, setOwnerCode] = useState("");

  const [loading, setLoading] = useState(false);

  // ✅ clave para refrescar tabla
  const [refreshKey, setRefreshKey] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const modalTitle =
    userSelect && userSelect.id && userSelect.id !== ""
      ? "Editar Usuario"
      : "Crear Usuario";

  const username_admin =
    JSON.parse(localStorage.getItem("siam-user-data") || "{}")?.username || "";

  const restricciones = obtenerProcesosPorPerfilYMenu();

  const alta = restricciones?.alta ?? true;
  const baja = restricciones?.baja ?? true;
  const modificacion = restricciones?.modificacion ?? true;

  const listName = "Usuarios";

  useEffect(() => {
    const usuarioLogeado = JSON.parse(localStorage.getItem("clientData") || "{}");
    setUrlCognito(usuarioLogeado.url_cognito || "");
    setOwnerCode(usuarioLogeado.owner_code || "");
  }, []);

  /* ===========================
     HANDLERS ESTABLES
  =========================== */

  const stableSetCreateButton = useCallback((value) => {
    setCreateModalOpen(value);

    // si se cierra el modal, limpiamos selección
    if (value === false) {
      setUserSelect({});
    }
  }, []);

  const stableSetDataEdit = useCallback((row) => {
    setUserSelect(row);
    setCreateModalOpen(true); // ✅ abre modal al editar
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    setUserSelect({});
  }, []);

  const handleOpenSectorModal = useCallback((user) => {
    setUserSelect(user);
    setSectorModalOpen(true);
  }, []);

  const handleCloseSectorModal = useCallback(() => {
    setSectorModalOpen(false);
    setUserSelect({});
  }, []);

  /* ===========================
     COLUMNAS
  =========================== */

  const columns = useMemo(() => {
    return [
      { field: "nombre", headerName: "Nombre", flex: 1 },
      { field: "apellido", headerName: "Apellido", flex: 1 },
      { field: "username", headerName: "Username", flex: 1 },
      { field: "email", headerName: "Email", flex: 1 },
      {
        field: "id",
        headerName: "Sectores",
        headerAlign: "center",
        flex: 1,
        renderCell: (params) => (
          <Grid
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleOpenSectorModal(params.row)}
              disabled={!params.row?.relations?.usuarios_cuentas}
            >
              {params.row?.relations?.usuarios_cuentas
                ? "Ver Sectores"
                : "No Sectores"}
            </Button>
          </Grid>
        ),
      },
    ];
  }, [handleOpenSectorModal]);

  /* ===========================
     OPTIONS
  =========================== */

  const mobileComponent = useCallback(({ row }) => (
    <GenericCard
      row={row}
      columns={columns}
      onEdit={(item) => {
        stableSetDataEdit(item);
      }}
      onDelete={baja ? async (id) => {
        const success = await deleteUser(id, { row });
        if (success) {
          setRefreshKey((prev) => prev + 1);
        }
      } : null}
    />
  ), [columns, stableSetDataEdit, baja]);

  const options = useMemo(() => {
    return {
      cols: columns,
      get: getUsers2,
      getPaginated: getUsersPaginated,
      pageSize: [5, 10, 25],
      edit: modificacion,
      delete: baja ? deleteUser : false,
      limit: 50,
      filter: true,
      exportData: true,
      server: true,

      createButton: false,
      setCreateButton: stableSetCreateButton,
      setDataEdit: stableSetDataEdit,
      mobileComponent: mobileComponent,

      hiddenButtonAgregar: !alta ? "true" : false,
    };
  }, [columns, modificacion, baja, alta, stableSetCreateButton, stableSetDataEdit, mobileComponent]);

  const mobileFabs = isMobile && alta && (
    <div style={{ position: 'fixed', bottom: 56, right: 20, display: 'flex', flexDirection: 'column', gap: '10px', zIndex: 9999 }}>
      <Fab
        color="primary"
        aria-label="add"
        onClick={() => stableSetCreateButton(true)}
      >
        <AddIcon />
      </Fab>
    </div>
  );

  /* ===========================
     SYNC COGNITO
  =========================== */

  function mapUsuariosParaCognito(usuarios) {
    return usuarios.map((u) => ({
      username: u.username,
      email: u.email,
      nombre: u.nombre,
      apellido: u.apellido,
      profile: u.id_perfiles === "1" ? "admin" : "user",
      password: u.username + "12345",
    }));
  }

  const handleSync = async () => {
    const confirm = await Swal.fire({
      title: "⚠ Sincronizar usuarios",
      text: "Esto borrará los usuarios de Cognito y los recreará desde la base local",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, sincronizar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);

      const usuarios_list = await getUsers2();
      const usuarios_cognito = mapUsuariosParaCognito(usuarios_list);

      const BATCH_SIZE = 5;
      let offset = 0;
      let remaining = usuarios_cognito.length;

      while (remaining > 0) {
        const response = await fetch(url_cognito, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-admin-key": "SIAM_SYNC_2025_9fA3kLxPq",
          },
          body: JSON.stringify({
            action: "syncUsers",
            ownerCode: owner_code,
            users: usuarios_cognito,
            offset,
            limit: BATCH_SIZE,
            dryRun: false,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Error en batch");
        }

        offset += BATCH_SIZE;
        remaining = data.remaining;
      }
      setLoading(false);
      await Swal.fire({
        icon: "success",
        title: "Sincronización completa",
        text: "Todos los usuarios fueron procesados correctamente",
      });
    } catch (err) {
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ===========================
     FORM CHILD
  =========================== */

  const dialogChild = useMemo(() => {
    return (
      <UserForm
        data={userSelect}
        setCreateButton={stableSetCreateButton} // ✅ CORRECTO
        setSnackbarVisible={setSnackbarVisible}
        setMessage={setMessage}
        setSeverity={setSeverity}
        onSuccess={() => setRefreshKey((prev) => prev + 1)} // ✅ REFRESH GRILLA
      />
    );
  }, [userSelect, stableSetCreateButton]);

  return (
    <Grid sx={{ mb: "50px", pt: 2 }}>
      {/* Modal Crear/Editar */}
      <SimpleDialogComponent
        open={createModalOpen}
        modalName={modalTitle}
        child={dialogChild}
        handleClose={handleCloseCreateModal} // ✅ CORRECTO
        buttonEnviar={false}
        buttonCancelar={true}
      />

      {/* Modal Sectores */}
      <SimpleDialogComponent
        open={sectorModalOpen}
        modalName={"Sectores del Usuario"}
        child={
          <ABMGenerico
            modelo={"usuarios_cuentas_localizaciones"}
            titulo={"Sectores"}
            condiciones={[
              { propiedad: "id_usuarios", valor: userSelect.id, operador: "=" },
            ]}
            joins={[
              {
                modelo: "cuentas_localizaciones",
                id_relacion: "id",
                id: "id_cuentas_localizaciones",
                operador: "=",
              },
            ]}
            valuesDefaultForm={{ id_usuarios: userSelect.id }}
            condiciones_select={{
              cuentas_localizaciones: userSelect?.relations?.usuarios_cuentas
                ? esArray(userSelect.relations.usuarios_cuentas).map((item) => ({
                  id_cuentas: item.id_cuentas,
                }))
                : [],
            }}
          />
        }
        handleClose={handleCloseSectorModal}
        buttonEnviar={false}
        buttonCancelar={true}
      />

      <PageHeaderCard title={listName} icon={<PeopleIcon />} />

      {/* Caja Sync Cognito */}
      {username_admin === "uadmin" && (
        <Box
          sx={{
            p: 3,
            mb: 3,
            width: "100%",
            maxWidth: 900,
            mx: "auto",
            borderRadius: 2,
            border: "2px solid",
            borderColor: "warning.main",
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 152, 0, 0.08)"
                : "rgba(255, 243, 205, 0.9)",
            textAlign: "center",
          }}
        >
          <WarningAmberIcon color="warning" sx={{ fontSize: 52, mb: 1 }} />

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.warning.light
                  : theme.palette.warning.dark,
            }}
          >
            Sincronización de usuarios Cognito
          </Typography>

          <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
            Esta acción es <strong>crítica</strong>. Elimina los usuarios actuales del
            pool de Cognito y los recrea desde la base de datos local.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Todos los usuarios deberán <strong>cambiar su contraseña</strong> en el
            próximo inicio de sesión.
          </Typography>

          <Button
            variant="contained"
            color="error"
            onClick={handleSync}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Sincronizar Usuarios Cognito
          </Button>
        </Box>
      )}

      <LoadingOverlay open={loading} />

      {/* TABLA */}
      <ListTablePaginated key={refreshKey} options={options} />

      {mobileFabs}

      <SnackbarMessenger
        open={snackbarVisible}
        message={message}
        severity={severity}
        close={() => setSnackbarVisible(false)}
      />
    </Grid>
  );
};
